/**
 * Analytics tab IA (Paper → App → "Home + Analytics", Oct 2026): five tabs.
 * Old slugs stay routable and redirect into the page that now holds them, so
 * deep links, bookmarks and shared links survive.
 */

export interface AnalyticsTab {
  id: string
  label: string
  /** Route segment under the property / workspace / share base path */
  slug: string
}

export const ANALYTICS_TABS: readonly AnalyticsTab[] = [
  { id: "overview", label: "Overview", slug: "traffic" },
  { id: "pages", label: "Pages", slug: "pages" },
  { id: "channels", label: "Traffic sources", slug: "channels" },
  { id: "devices", label: "Devices & locations", slug: "devices" },
  { id: "clicks", label: "Clicks & forms", slug: "clicks" },
]

/** Share links keep their extra tabs (no sidebar there). */
export const SHARE_EXTRA_TABS: readonly AnalyticsTab[] = [
  { id: "heatmaps", label: "Heatmaps", slug: "heatmaps" },
  { id: "split-testing", label: "Experiments", slug: "split-testing" },
  { id: "funnels", label: "Funnels", slug: "funnels" },
]

export function analyticsTabsFor(isShareMode: boolean): AnalyticsTab[] {
  return isShareMode ? [...ANALYTICS_TABS, ...SHARE_EXTRA_TABS] : [...ANALYTICS_TABS]
}

/**
 * Retired tab slug → the tab that now holds its content. `params` renames
 * query params whose meaning moved (the forms table's expanded row).
 */
export const ANALYTICS_REDIRECTS: Readonly<Record<string, { to: string; params?: Record<string, string> }>> = {
  locations: { to: "devices" },
  "llm-referrals": { to: "channels" },
  forms: { to: "clicks", params: { detailPage: "formDetailPage" } },
  bots: { to: "channels" },
  "insights/forms": { to: "clicks", params: { detailPage: "formDetailPage" } },
}

/** Query param the Forms section uses for its expanded row (Clicks owns `detailPage`). */
export const FORM_DETAIL_PARAM = "formDetailPage"

/**
 * Where a retired analytics URL lands. Keeps the base path (property, workspace
 * or share link), the date range and filters in the query, and the hash.
 * Returns null when the path is not a retired slug.
 */
export function resolveAnalyticsRedirect(
  pathname: string,
  search = "",
): { pathname: string; search: string } | null {
  const trimmed = pathname.replace(/\/+$/, "")
  // Longest slug first, so `insights/forms` wins over `forms`.
  const rules = Object.entries(ANALYTICS_REDIRECTS).sort(([a], [b]) => b.length - a.length)
  for (const [from, rule] of rules) {
    if (!trimmed.endsWith(`/${from}`)) continue
    const base = trimmed.slice(0, -from.length - 1)
    const params = new URLSearchParams(search)
    for (const [oldKey, newKey] of Object.entries(rule.params ?? {})) {
      const value = params.get(oldKey)
      if (value === null) continue
      params.delete(oldKey)
      params.set(newKey, value)
    }
    const qs = params.toString()
    return { pathname: `${base}/${rule.to}`, search: qs ? `?${qs}` : "" }
  }
  return null
}
